import { Box, Group, Text } from "@mantine/core";

import { fmtNum } from "./ui";

export const PALETTE = ["#2f9e44", "#1c7ed6", "#f08c00", "#ae3ec9", "#e03131", "#0ca678", "#5c7cfa", "#868e96"];

export interface Series { key: string; label: string; color?: string }

/** Jednostavan SVG grafik (stubičasti / linijski) bez dodatnih biblioteka. */
export function Chart({ rows, x, series, type = "bar", height = 260, xLabel }: {
  rows: Record<string, unknown>[]; x: string; series: Series[]; type?: "bar" | "line"; height?: number; xLabel?: (v: unknown) => string;
}) {
  const W = 800, H = height, L = 56, R = 12, T = 12, B = 64;
  const data = rows.slice(0, 60);
  if (!data.length || !series.length) return <Text c="dimmed" size="sm">—</Text>;
  const vals = series.map((s) => data.map((r) => Number(r[s.key] ?? 0) || 0));
  const max = nice(Math.max(1e-9, ...vals.flat()) / 4) * 4;
  const w = W - L - R, h = H - T - B, step = w / data.length;
  const y = (v: number) => T + h - (h * v) / max;
  return (
    <Box>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ fontFamily: "inherit", fontSize: 11 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <line x1={L} x2={L + w} y1={y((max * i) / 4)} y2={y((max * i) / 4)} stroke="var(--mantine-color-default-border)" />
            <text x={L - 6} y={y((max * i) / 4) + 4} textAnchor="end" fill="var(--mantine-color-dimmed)">{fmtNum((max * i) / 4, max < 10 ? 1 : 0)}</text>
          </g>
        ))}
        {type === "bar"
          ? vals.map((v, si) => v.map((val, i) => {
            const bw = (step * 0.8) / vals.length;
            return (
              <rect key={`${si}-${i}`} x={L + step * i + step * 0.1 + bw * si} y={y(val)} width={bw} height={T + h - y(val)}
                fill={series[si].color ?? PALETTE[si % 8]} rx={2}>
                <title>{`${xLabel ? xLabel(data[i][x]) : String(data[i][x])} — ${series[si].label}: ${fmtNum(val)}`}</title>
              </rect>
            );
          }))
          : vals.map((v, si) => (
            <g key={si}>
              <polyline fill="none" stroke={series[si].color ?? PALETTE[si % 8]} strokeWidth={2}
                points={v.map((val, i) => `${L + step * (i + 0.5)},${y(val)}`).join(" ")} />
              {v.map((val, i) => (
                <circle key={i} cx={L + step * (i + 0.5)} cy={y(val)} r={3} fill={series[si].color ?? PALETTE[si % 8]}>
                  <title>{`${String(data[i][x])} — ${series[si].label}: ${fmtNum(val)}`}</title>
                </circle>
              ))}
            </g>
          ))}
        {data.map((r, i) => (
          <text key={i} transform={`translate(${L + step * (i + 0.5)},${T + h + 12}) rotate(35)`} fill="var(--mantine-color-text)">
            {(xLabel ? xLabel(r[x]) : String(r[x] ?? "")).slice(0, 16)}
          </text>
        ))}
      </svg>
      {series.length > 1 && (
        <Group gap="md" justify="center">
          {series.map((s, i) => (
            <Group key={s.key} gap={4}><Box w={10} h={10} bg={s.color ?? PALETTE[i % 8]} style={{ borderRadius: 2 }} /><Text size="xs">{s.label}</Text></Group>
          ))}
        </Group>
      )}
    </Box>
  );
}

function nice(v: number): number {
  const e = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * e) return m * e;
  return 10 * e;
}

/** Pivot vremenske serije {period, serija, vrijednost} -> redovi po periodu sa kolonom po seriji. */
export function pivot(rows: { period: string; serija: string; vrijednost: number | null }[]) {
  const keys = Array.from(new Set(rows.map((r) => r.serija)));
  const by = new Map<string, Record<string, unknown>>();
  for (const r of rows) {
    const o = by.get(r.period) ?? { period: r.period };
    o[r.serija] = r.vrijednost ?? 0;
    by.set(r.period, o);
  }
  return { rows: Array.from(by.values()), series: keys.map((k) => ({ key: k, label: k })) };
}
